import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AFPTable,
  EmptyState,
  Pagination,
  Spinner,
  TextInput,
  SelectDropdown,
  useModal,
} from '@gsa/afp-component-library';
import { emDashUnicode } from '@gsa/afp-shared-form-utils';
import { useDispatch, useSelector } from 'react-redux';
import AdditionalRequestStatusBadge from './additionals-status-badge';
import { AdditionalRequestSortMapping } from './utils/additional-utils';
import {
  useGetAdditionalVehicleList,
  useUpdateFundingStatus,
} from '../../requests/additionals-request';
import {
  setAdditionalRequestList,
  setPaginationState,
} from '../../reducers/additionals-reducer';
import { ADDITIONAL_REQUEST_STATUS } from './utils/additional-utils';
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';
import { UserTypes, UserRoles } from '../../constants/index.jsx';
import ConfirmFundingModal from './components/confirm-funding/confirm-funding-modal.jsx';
import { Tooltip, Icon } from '@gsa/afp-component-library';
const tableRef = React.createRef();

const AdditionalRequestTable = () => {
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUser();

  const seeFundingOptionsAndCheckBoxes = currentUser?.roles?.some(
    (role) =>
      role.name === UserRoles.OVERSIGHT_BUSINESS_ANALYST ||
      role.name === UserRoles.SITE_ADMIN,
  );
  const { additionalRequestList, additionalRequestFilters, paginationState } =
    useSelector((state) => state.additionalsReducer);
  const [updateFundingStatus] = useUpdateFundingStatus();
  const [getAdditionalVehicleList, { loading: additionalVehicleListLoading }] =
    useGetAdditionalVehicleList();
  const [order, setOrder] = useState(
    paginationState?.order || [['created_at', 'DESC']],
  );
  const [globalFilter, setGlobalFilter] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  const [selectedTableRows, setSelectedTableRows] = useState([]);
  const [selectedFunding, setSelectedFunding] = useState('-1');
  const [selectedFundingLabel, setSelectedFundingLabel] = useState('');

  const { isOpen, closeModal, openModal } = useModal();

  // apply backend sort only when fetching the initial time
  const modifiedOrder = useMemo(() => {
    if (!paginationState?.order) return [['created_at', 'DESC']];
    const [sortColumnName, sortIndicator] = paginationState.order[0];
    const sortKey =
      AdditionalRequestSortMapping[sortColumnName.trim().replace(/`/g, '')];
    return [[sortKey, sortIndicator]];
  }, [paginationState?.order]);

  const fetchAdditionalsFiltersData = () => {
    getAdditionalVehicleList({
      variables: {
        limit: paginationState.limit,
        offset: paginationState.offset,
        order: modifiedOrder,
        filters: {
          conditions: additionalRequestFilters,
          operator: '$and',
        },
      },
    }).then(({ data }) => {
      setSelectedFunding('-1');
      dispatch(setAdditionalRequestList(data));
      setFilteredData(data?.rows);
      dispatch(
        setPaginationState({
          ...paginationState,
          currentPage: 1,
        }),
      );
    });
  };

  // Fetch data from backend only when filters change
  useEffect(() => {
    if (additionalRequestFilters.length === 0) {
      setFilteredData([]);
    } else {
      fetchAdditionalsFiltersData();
    }
  }, [additionalRequestFilters]);

  // Handle global search
  useEffect(() => {
    if (!additionalRequestList?.rows) return;

    if (globalFilter === '') {
      setFilteredData(additionalRequestList.rows);
      return;
    }

    const searchTerm = globalFilter.toLowerCase();
    const columnsToInclude = [
      'additionalRequestNumber',
      'boac',
      'customerName',
      'customerId',
      'sin',
      'requestType',
      'requestStatusCode',
      'email',
    ];

    const searchResults = additionalRequestList.rows.filter((row) =>
      columnsToInclude.some((key) => {
        const value = row[key];
        return value && value.toString().toLowerCase().includes(searchTerm);
      }),
    );

    setFilteredData(searchResults);
    handlePaginationChange(1, paginationState.itemsPerPage);
  }, [globalFilter, additionalRequestList]);

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: 'Additional request ID',
        accessor: 'additionalRequestNumber',
        sortable: true,
        Cell: ({ value, row: { original } }) => (
          <Link
            to={`/lease/additionals/request/${encodeURIComponent(
              original.additionalRequestId,
            )}`}
          >
            {value}
          </Link>
        ),
      },
      {
        Header: 'BOAC',
        accessor: 'boac',
        sortable: true,
      },
      {
        Header: 'Customer account number',
        accessor: 'customerId',
        sortable: true,
      },
      {
        Header: 'Requested SIN',
        accessor: 'sin',
        sortable: true,
      },
      {
        Header: 'Request type',
        accessor: 'requestType',
        sortable: true,
      },
      {
        Header: 'Request status',
        accessor: 'requestStatusCode',
        sortable: true,
        Cell: ({ row: { original } }) => {
          return original?.requestStatusCode ? (
            <AdditionalRequestStatusBadge
              requestStatus={original.requestStatusCode}
            />
          ) : (
            emDashUnicode
          );
        },
      },
    ];

    return columnList;
  }, []);

  const paginatedData = useMemo(() => {
    const { currentPage, itemsPerPage } = paginationState;
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;

    return {
      rows: filteredData.slice(start, end),
      count: filteredData.length,
    };
  }, [filteredData, paginationState]);

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    dispatch(
      setPaginationState({
        ...paginationState,
        currentPage,
        itemsPerPage,
      }),
    );
  };

  const sortDataByColumn = (data, sortValue) => {
    const updatedSortValue = sortValue;
    if (!updatedSortValue?.length) return;

    const sortColumnName = updatedSortValue[0][0].trim().replace(/`/g, '');
    const sortIndicator = updatedSortValue[0][1];

    const sortedData = [...data].sort((a, b) => {
      const aValue = a[sortColumnName] || '';
      const bValue = b[sortColumnName] || '';

      return sortIndicator === 'ASC'
        ? aValue.toString().localeCompare(bValue.toString())
        : bValue.toString().localeCompare(aValue.toString());
    });
    return sortedData;
  };

  const onSort = (sortValue) => {
    const sortedData = sortDataByColumn(filteredData, sortValue);
    dispatch(
      setPaginationState({
        ...paginationState,
        order: sortValue,
      }),
    );
    setOrder(sortValue);
    setFilteredData(sortedData);
  };

  const getSelectableRows = () => {
    const data = paginatedData.rows
      .map((row, index) =>
        row.requestStatusCode !==
          ADDITIONAL_REQUEST_STATUS.PENDING_GSA_APPROVAL &&
        row.requestStatusCode !== ADDITIONAL_REQUEST_STATUS.APPROVED_AT_FSR
          ? index
          : null,
      )
      .filter((index) => index !== null);
    return data;
  };

  const fundingOptionsPendingGSAApproval = [
    {
      label: '-Select-',
      value: '-1',
    },
    {
      label: 'Mark as funded',
      value: 'FUNDED',
    },
    {
      label: 'Mark as not funded',
      value: 'NOT_FUNDED',
    },
  ];

  const fundingOptionsApprovedAtFSR = [
    {
      label: '-Select-',
      value: '-1',
    },
    {
      label: 'Rescind funding',
      value: 'NOT_FUNDED',
    },
  ];

  const handleContinue = (data) => {
    const ids = selectedTableRows.map((row) => row.additionalRequestId);
    updateFundingStatus({
      variables: {
        additionalRequestIds: ids,
        fundingStatusReason: data?.reason || null,
        fundingStatus: selectedFunding,
      },
    }).then(({ data }) => {
      closeModal();
      fetchAdditionalsFiltersData();
    });
  };

  return (
    <>
      <div className="padding-top-5">
        <div className="display-flex grid-row flex-align-end">
          {seeFundingOptionsAndCheckBoxes && (
            <div className="assign-funding-dropdown padding-bottom-2">
              <SelectDropdown
                id="assignFundingStatus"
                onChange={(e) => {
                  if (e.target.value !== '-1') {
                    setSelectedFunding(e.target.value);
                    setSelectedFundingLabel(e.target.textContent);
                    openModal();
                  }
                }}
                value={selectedFunding}
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div>Assign funding status</div>
                    <Tooltip
                      className="usa-button--unstyled"
                      label="To assign funding, all selected records must be in the same status."
                      position="right"
                    >
                      <Icon
                        iconName="info"
                        className="text-primary usa-icon--size-3 margin-left-05"
                      />
                    </Tooltip>
                  </div>
                }
                data-testid="assign-funding-status"
                options={
                  selectedTableRows?.length > 0 &&
                  selectedTableRows.every(
                    (row) =>
                      row.requestStatusCode ===
                      ADDITIONAL_REQUEST_STATUS.PENDING_GSA_APPROVAL,
                  )
                    ? fundingOptionsPendingGSAApproval
                    : selectedTableRows?.length > 0 &&
                      selectedTableRows.every(
                        (row) =>
                          row.requestStatusCode ===
                          ADDITIONAL_REQUEST_STATUS.APPROVED_AT_FSR,
                      )
                    ? fundingOptionsApprovedAtFSR
                    : [
                        {
                          label: '-Select-',
                          value: '-1',
                        },
                      ]
                }
              />
            </div>
          )}
          <div className="tablet:grid-col-4 margin-left-auto">
            <TextInput
              type="text"
              onChange={(e) => setGlobalFilter(e.target.value)}
              value={globalFilter}
              name="table-search"
              placeholder="Search within results"
              data-testid="referral-table-search"
              inputClass="description-textarea"
            />
          </div>
        </div>

        <AFPTable
          fullWidth
          ref={tableRef}
          testId="additional-request-listing-table"
          columns={columns}
          defaultSort={order}
          onSort={onSort}
          data={!additionalVehicleListLoading ? paginatedData.rows : []}
          selectable={seeFundingOptionsAndCheckBoxes}
          selectDisabledRows={getSelectableRows()}
          onRowSelect={({ selectedFlatRows }) => {
            const selectedAdditionalsRows = selectedFlatRows?.map(
              (row) => row.original,
            );
            setSelectedTableRows(selectedAdditionalsRows);
          }}
        />
        {additionalVehicleListLoading && <Spinner className="padding-y-9" />}
        {paginatedData.rows.length > 0 && (
          <Pagination
            fullWidth
            variant="advanced"
            itemsPerPageOptions={[10, 15, 20, 25, 50, 100]}
            itemsCount={filteredData.length}
            itemsPerPage={paginationState.itemsPerPage}
            currentPage={paginationState.currentPage}
            onPageChange={handlePaginationChange}
            isReset={paginationState.isReset}
          />
        )}
        {!paginatedData.rows.length && !additionalVehicleListLoading && (
          <div className="bg-base-lightest padding-y-5">
            <div className="text-center padding-y-4">
              <EmptyState alt="Image not available" hasBackground />
            </div>
            <div
              className="text-center text-bold"
              data-testid="vehicles-referrals-no-data"
            >
              No additional requests available
            </div>
          </div>
        )}
      </div>

      <ConfirmFundingModal
        isOpen={isOpen}
        handleCancel={() => {
          setSelectedFunding('-1');
          closeModal();
        }}
        selectedRows={selectedTableRows}
        selectedFunding={selectedFunding}
        selectedFundingLabel={selectedFundingLabel}
        handleContinue={handleContinue}
      />
    </>
  );
};

export default AdditionalRequestTable;
